export const DEBUG_PANEL_SECRET_WORD = "folloadmin";

type SecretWordKey = Pick<
  KeyboardEvent,
  "key" | "code" | "ctrlKey" | "altKey" | "metaKey"
>;

const MODIFIER_KEYS = new Set([
  "Shift",
  "Control",
  "Alt",
  "Meta",
  "CapsLock",
  "AltGraph",
]);

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/**
 * The Latin letter a key press typed. `key` is what the layout typed, so AZERTY
 * and other Latin layouts work as printed; when it is not a Latin letter (a
 * Russian layout types Cyrillic), the physical key's QWERTY letter is used, so
 * the word can be typed without switching layouts.
 */
const typedLetter = ({ key, code }: SecretWordKey): string | null => {
  if (/^[a-z]$/i.test(key)) {
    return key.toLowerCase();
  }

  const physical = /^Key([A-Z])$/.exec(code);

  return physical ? physical[1].toLowerCase() : null;
};

/**
 * What has been typed towards `word` after one more key press: the tail of the
 * letters typed so far, at most as long as the word. The word is complete once
 * the result equals it.
 *
 * Only plain letters count. A shortcut (Ctrl, Alt or Meta held) or any other
 * key starts over; modifier keys on their own are ignored, so Shift does not
 * break a word typed in capitals.
 */
export const nextSecretWordProgress = (
  typed: string,
  event: SecretWordKey,
  word: string,
): string => {
  if (MODIFIER_KEYS.has(event.key)) {
    return typed;
  }

  if (event.ctrlKey || event.altKey || event.metaKey) {
    return "";
  }

  const letter = typedLetter(event);

  return letter ? `${typed}${letter}`.slice(-word.length) : "";
};

/** Whether a key press lands in a field the visitor is typing into. */
export const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!target) {
    return false;
  }

  const element = target as Partial<HTMLElement>;

  return (
    Boolean(element.isContentEditable) ||
    EDITABLE_TAGS.has(element.tagName ?? "")
  );
};
